'use client';
import { useState, useCallback, useRef, useEffect } from 'react';
import { logAuditEvent } from '@/lib/auditLogger';

export interface RetryFetchOptions {
  /** Maximum number of retry attempts (default: 3) */
  maxRetries?: number;
  /** Base delay in ms for exponential backoff (default: 500) */
  baseDelay?: number;
  /** Maximum delay cap in ms (default: 8000) */
  maxDelay?: number;
  /** Jitter factor 0–1 added to each delay (default: 0.3) */
  jitter?: number;
  /** Whether to log failures to the audit trail (default: false) */
  auditOnFailure?: boolean;
  /** Audit resource type label (e.g. 'student_dashboard') */
  auditResourceType?: string;
}

export interface RetryFetchState {
  loading: boolean;
  error: string | null;
  retryCount: number;
  retrying: boolean;
}

/**
 * Wraps an async fetch function with exponential-backoff retry logic.
 *
 * Usage:
 *   const { loading, error, retryCount, retrying, execute } = useRetryFetch(fetchData, { maxRetries: 3 });
 *   useEffect(() => { execute(); }, [execute]);
 */
export function useRetryFetch<T>(
  fetchFn: () => Promise<T>,
  options: RetryFetchOptions = {}
): RetryFetchState & { execute: () => Promise<T | undefined> } {
  const {
    maxRetries = 3,
    baseDelay = 500,
    maxDelay = 8000,
    jitter = 0.3,
    auditOnFailure = false,
    auditResourceType = 'data_fetch',
  } = options;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [retrying, setRetrying] = useState(false);
  const attemptRef = useRef(0);

  const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

  const getDelay = (attempt: number): number => {
    const exponential = Math.min(baseDelay * Math.pow(2, attempt), maxDelay);
    const jitterAmount = exponential * jitter * Math.random();
    return Math.round(exponential + jitterAmount);
  };

  const execute = useCallback(async (): Promise<T | undefined> => {
    setLoading(true);
    setError(null);
    attemptRef.current = 0;

    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      attemptRef.current = attempt;

      if (attempt > 0) {
        setRetrying(true);
        setRetryCount(attempt);
        const delay = getDelay(attempt - 1);
        await sleep(delay);
      }

      try {
        const result = await fetchFn();
        setError(null);
        setRetrying(false);
        setLoading(false);
        setRetryCount(0);
        return result;
      } catch (err: unknown) {
        lastError = err instanceof Error ? err : new Error(String(err));

        // Don't retry on the last attempt
        if (attempt === maxRetries) break;
      }
    }

    // All attempts exhausted
    const errorMessage = lastError?.message ?? 'An unknown error occurred';
    setError(errorMessage);
    setRetrying(false);
    setLoading(false);
    setRetryCount(maxRetries);

    // Surface to audit trail if requested
    if (auditOnFailure) {
      logAuditEvent({
        eventCategory: 'data',
        eventAction: 'fetch_failure',
        resourceType: auditResourceType,
        description: `Data fetch failed after ${maxRetries} retries: ${errorMessage}`,
        severity: 'warning',
        metadata: {
          retries: maxRetries,
          error: errorMessage,
          timestamp: new Date().toISOString(),
        },
      }).catch(() => {/* audit logging must never throw */});
    }

    return undefined;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchFn, maxRetries, baseDelay, maxDelay, jitter, auditOnFailure, auditResourceType]);

  return { loading, error, retryCount, retrying, execute };
}
